import { useState } from 'react';
import { ArrowUpRight, ArrowRight, Globe, Code, Smartphone, Monitor, ExternalLink } from 'lucide-react';

const ProjectCard = ({ 
  title, 
  subtitle, 
  platform, 
  industry, 
  tags, 
  imageUrl,
  primaryColor,
  gradientFrom,
  gradientTo,
  isEven
}) => {
  return (
    <div className={`group cursor-pointer transition-all duration-700 hover:-translate-y-2 ${isEven ? 'mt-8 lg:mt-16' : 'mt-0'}`}>
      <div className="relative overflow-hidden rounded-3xl bg-white shadow-lg hover:shadow-2xl transition-all duration-700 border-0 backdrop-blur-sm">
        {/* Gradient Border Animation */}
        <div 
          className="absolute inset-0 rounded-3xl p-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo}, ${primaryColor})`
          }}
        >
          <div className="bg-white rounded-3xl h-full w-full"></div>
        </div>

        {/* Image Container with Overlay */}
        <div className="relative overflow-hidden h-56 rounded-t-3xl">
          <img 
            src={imageUrl} 
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div 
            className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity duration-500"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}40, ${gradientFrom}30, ${gradientTo}20)`
            }}
          ></div>
          
          {/* Floating Color Indicator */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full shadow-lg border-2 border-white/50 animate-pulse"
              style={{ backgroundColor: primaryColor }}
            ></div>
            <div 
              className="w-3 h-3 rounded-full shadow-md border border-white/30"
              style={{ backgroundColor: gradientFrom }}
            ></div>
            <div 
              className="w-2 h-2 rounded-full shadow-sm border border-white/20"
              style={{ backgroundColor: gradientTo }}
            ></div>
          </div>
        </div>

        {/* Content Section */}
        <div className="relative p-6 bg-white rounded-b-3xl">
          {/* Title with Color Accent */}
          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors duration-300">
              {title}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {subtitle}
            </p>
          </div>

          {/* Platform & Industry with Custom Colors */}
          <div className="flex flex-col gap-3 mb-4 text-sm">
            <div className="flex items-center gap-3">
              <div 
                className="w-3 h-3 rounded-full shadow-sm"
                style={{ backgroundColor: primaryColor }}
              ></div>
              <span className="font-semibold text-gray-700">Platform:</span>
              <span 
                className="font-medium px-2 py-1 rounded-lg text-white text-xs"
                style={{ backgroundColor: `${primaryColor}CC` }}
              >
                {platform}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div 
                className="w-3 h-3 rounded-full shadow-sm"
                style={{ backgroundColor: gradientFrom }}
              ></div>
              <span className="font-semibold text-gray-700">Industry:</span>
              <span 
                className="font-medium px-2 py-1 rounded-lg text-white text-xs"
                style={{ backgroundColor: `${gradientFrom}CC` }}
              >
                {industry}
              </span>
            </div>
          </div>

          {/* Tags with Gradient Backgrounds */}
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-2 text-white rounded-full text-xs font-medium shadow-sm transition-all duration-300 hover:scale-105 hover:shadow-md"
                style={{
                  background: index % 3 === 0 
                    ? `linear-gradient(135deg, ${primaryColor}, ${gradientFrom})` 
                    : index % 3 === 1 
                    ? `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` 
                    : `linear-gradient(135deg, ${gradientTo}, ${primaryColor})`
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Animated Bottom Accent */}
          <div 
            className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-700 rounded-b-3xl"
            style={{
              background: `linear-gradient(90deg, ${primaryColor}, ${gradientFrom}, ${gradientTo})`
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const PortfolioShowcase = () => {
  // const projects = [
  //   {
  //     title: "Mobile App for Finance Event",
  //     subtitle: "Exclusive finance event application built in 13 weeks with real-time features",
  //     platform: "Mobile App",
  //     industry: "Finance & Events",
  //     tags: ["UI/UX Design", "Mobile Development", "Real-time Features"],
  //     primaryColor: "#FF6B6B",
  //     gradientFrom: "#4ECDC4",
  //     gradientTo: "#45B7D1",
  //     imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Taaza%20panta.jpg?updatedAt=1750844073582"
  //   },
  //   {
  //     title: "Multi-Layered KYC Solution",
  //     subtitle: "Comprehensive KYC solution for Nigeria's leading fintech application",
  //     platform: "Web & Mobile",
  //     industry: "Fintech & Banking",
  //     tags: ["KYC Integration", "Security", "Compliance"],
  //     primaryColor: "#A8E6CF",
  //     gradientFrom: "#88D8A3",
  //     gradientTo: "#5CB85C",
  //     imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Finsta.jpg?updatedAt=1750844073374"
  //   },
  //   {
  //     title: "Silk Design System",
  //     subtitle: "Complete design system achieving 50% efficiency gains across projects",
  //     platform: "Design System",
  //     industry: "Design & Development",
  //     tags: ["Design System", "Component Library", "Efficiency"],
  //     primaryColor: "#FFD93D",
  //     gradientFrom: "#FFA502",
  //     gradientTo: "#FF6348",
  //     imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Rental.jpg?updatedAt=1750844073335"
  //   },
  //   {
  //     title: "E-Commerce Platform",
  //     subtitle: "Full-stack shopping solution with advanced payment integration",
  //     platform: "Web & Mobile",
  //     industry: "Retail & E-commerce",
  //     tags: ["E-commerce", "Payment Gateway", "Inventory"],
  //     primaryColor: "#6C5CE7",
  //     gradientFrom: "#A29BFE",
  //     gradientTo: "#74B9FF",
  //     imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/IT.jpg?updatedAt=1750844073806"
  //   },
  //   {
  //     title: "Healthcare Management",
  //     subtitle: "Patient management system with appointment scheduling and HIPAA compliance",
  //     platform: "Web Application",
  //     industry: "Healthcare & Medical",
  //     tags: ["Patient Management", "Scheduling", "HIPAA Compliant"],
  //     primaryColor: "#00CEC9",
  //     gradientFrom: "#55EFC4",
  //     gradientTo: "#81ECEC",
  //     imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/health%20care.jpg?updatedAt=1750844073331"
  //   },
  //   {
  //     title: "Learning Management System",
  //     subtitle: "Online education platform with course management and analytics",
  //     platform: "Web & Mobile",
  //     industry: "Education & Training",
  //     tags: ["LMS", "Course Management", "Analytics"],
  //     primaryColor: "#FD79A8",
  //     gradientFrom: "#FDCB6E",
  //     gradientTo: "#E17055",
  //     imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Food%20delivery.jpg?updatedAt=1750844073840"
  //   }
  // ];


  const projects = [
    {
      title: "Organic Fruits Delivery Application",
      subtitle: "Fresh organic fruits delivery platform with real-time tracking and subscription management",
      platform: "Mobile App",
      industry: "Food & Agriculture",
      tags: ["Organic Products", "Delivery Tracking", "Subscription Model"],
      primaryColor: "#A8E6CF",
      gradientFrom: "#88D8A3",
      gradientTo: "#5CB85C",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Taaza%20panta.jpg?updatedAt=1750844073582"
    },
    {
      title: "NBFC Software",
      subtitle: "Comprehensive Non-Banking Financial Company solution with multi-layered KYC integration",
      platform: "Web & Mobile",
      industry: "Fintech & Banking",
      tags: ["KYC Integration", "Financial Services", "Compliance"],
      primaryColor: "#FF6B6B",
      gradientFrom: "#4ECDC4",
      gradientTo: "#45B7D1",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Finsta.jpg?updatedAt=1750844073374"
    },
    {
      title: "Real Estate ERP Application",
      subtitle: "Complete enterprise resource planning solution for real estate management and operations",
      platform: "Web Application",
      industry: "Real Estate & Property",
      tags: ["ERP System", "Property Management", "CRM Integration"],
      primaryColor: "#FFD93D",
      gradientFrom: "#FFA502",
      gradientTo: "#FF6348",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Rental.jpg?updatedAt=1750844073335"
    },
    {
      title: "LMS Platform",
      subtitle: "Advanced Learning Management System with course management, analytics and interactive features",
      platform: "Web & Mobile",
      industry: "Education & Training",
      tags: ["LMS", "Course Management", "Student Analytics"],
      primaryColor: "#6C5CE7",
      gradientFrom: "#A29BFE",
      gradientTo: "#74B9FF",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/IT.jpg?updatedAt=1750844073806"
    },
    {
      title: "Health Care Application",
      subtitle: "Comprehensive healthcare management system with patient records, appointments and HIPAA compliance",
      platform: "Web & Mobile",
      industry: "Healthcare & Medical",
      tags: ["Patient Management", "Medical Records", "HIPAA Compliant"],
      primaryColor: "#00CEC9",
      gradientFrom: "#55EFC4",
      gradientTo: "#81ECEC",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/health%20care.jpg?updatedAt=1750844073331"
    },
    {
      title: "Food Delivery Application",
      subtitle: "Full-featured food delivery platform with restaurant management, order tracking and payment integration",
      platform: "Mobile App",
      industry: "Food & Delivery",
      tags: ["Food Delivery", "Restaurant Management", "Order Tracking"],
      primaryColor: "#FD79A8",
      gradientFrom: "#FDCB6E",
      gradientTo: "#E17055",
      imageUrl: "https://ik.imagekit.io/kqumni8l2/Kapil%20Web%20Projects/Food%20delivery.jpg?updatedAt=1750844073840"
    }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 py-2 px-2 sm:px-4 lg:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Section with Gradient Background */}
        <div className="text-center mb-4">
          
          <h1 className="text-2xl sm:text-xl lg:text-3xl text-[#0e0e4e] bg-gradient-to-r from-gray-900 via-purple-900 to-blue-900 bg-clip-text text-transparent mb-8 leading-tight">
            Our Portfolio
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium">
            Check how we delivered tangible gains for clients across industries with innovative solutions.
          </p>
        </div>

        {/* Staggered Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {projects.map((project, index) => (
            <ProjectCard 
              key={index} 
              {...project} 
              isEven={index % 2 === 1}
            />
          ))}
        </div>

      
      </div>
    </div>
  );
};

export default PortfolioShowcase;